import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Printer, Stamp } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { StatusBadge, PriorityBadge, ConfBadge } from "../components/common/Badges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import WorkflowPanel from "../components/measure/WorkflowPanel";
import OverviewTab from "../components/measure/OverviewTab";
import VersionsTab from "../components/measure/VersionsTab";
import DocumentsTab from "../components/measure/DocumentsTab";
import RelationshipsTab from "../components/measure/RelationshipsTab";
import HistoryTab from "../components/measure/HistoryTab";
import PostApprovalTab from "../components/measure/PostApprovalTab";
import { PublicReleaseTab } from "../components/portal/ReleasePanel";

function Stepper({ stages, current }) {
  const cur = stages.find((s) => s.code === current);
  return (
    <div className="overflow-x-auto pb-1" data-testid="workflow-stepper">
      <ol className="flex min-w-max items-center gap-1">
        {stages.map((s) => {
          const done = cur && s.sequence < cur.sequence;
          const active = s.code === current;
          return (
            <li key={s.id} title={s.name} className={`flex h-6 items-center rounded px-2 text-[10px] font-medium ${active ? "bg-sky-600 text-white" : done ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-400"}`}>
              {s.sequence}. {s.name}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function MeasureDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const load = useCallback(() => {
    api.get(`/measures/${id}`).then((r) => { setM(r.data); setTick((t) => t + 1); }).catch((e) => setError(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="text-sm text-red-600" data-testid="measure-error">{error}</div>;
  if (!m) return <div className="text-sm text-slate-500">Loading…</div>;
  const s = m.summary;
  return (
    <div className="fade-up">
      <Link to="/registry/all" className="mb-3 inline-flex items-center text-xs text-slate-500 hover:text-sky-700"><ArrowLeft className="mr-1 h-3.5 w-3.5" />Registry</Link>
      <div className="mb-5 rounded-md border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-slate-500" data-testid="measure-tracking-ref">{m.tracking_ref}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-600">{s.measure_type}</span>
              <StatusBadge category={s.stage_category} name={s.stage_name} testId="measure-status-badge" />
              <PriorityBadge value={m.priority} /><ConfBadge value={m.confidentiality} />
              {m.is_locked && <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600" data-testid="measure-locked-indicator"><Lock className="h-3 w-3" />Text preserved</span>}
            </div>
            {m.official_number && <div className="mt-2 font-heading text-lg font-semibold text-sky-800" data-testid="measure-official-number">{m.official_number}</div>}
            <h1 className="mt-1 text-xl font-semibold leading-snug tracking-tight text-slate-900 md:text-2xl" data-testid="measure-title">{m.official_title || m.proposed_title}</h1>
            <div className="mt-2 text-[11px] text-slate-500">Last action {fmtDateTime(m.last_action_at)} · record version {m.row_version}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.open(`/measures/${m.id}/print`, "_blank")} data-testid="print-measure-button"><Printer className="mr-1.5 h-4 w-4" />Print / PDF</Button>
          {m.official_number && <Button variant="outline" size="sm" onClick={() => window.open(`/measures/${m.id}/certified-copy`, "_blank")} data-testid="ctc-open-button"><Stamp className="mr-1.5 h-4 w-4" />Certified true copy</Button>}
        </div>
        <div className="mt-4"><Stepper stages={m.workflow_stages} current={m.status} /></div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <Tabs defaultValue={params.get("tab") || "overview"} className="min-w-0">
          <TabsList className="mb-4 flex h-auto max-w-full flex-wrap justify-start bg-slate-100">
            {["overview", "text", "documents", "relationships", "post-approval", "public", "history"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize">{t === "text" ? "Text & versions" : t === "post-approval" ? "Post-approval" : t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview"><OverviewTab m={m} onSaved={load} /></TabsContent>
          <TabsContent value="text"><VersionsTab m={m} onSaved={load} /></TabsContent>
          <TabsContent value="documents"><DocumentsTab m={m} onSaved={load} /></TabsContent>
          <TabsContent value="relationships"><RelationshipsTab m={m} onSaved={load} /></TabsContent>
          <TabsContent value="post-approval"><PostApprovalTab m={m} onSaved={load} /></TabsContent>
          <TabsContent value="public"><PublicReleaseTab m={m} /></TabsContent>
          <TabsContent value="history"><HistoryTab id={m.id} tick={tick} /></TabsContent>
        </Tabs>
        <WorkflowPanel m={m} onDone={load} tick={tick} />
      </div>
    </div>
  );
}
